import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const QuizLinkDialog = ({ book, onClose, onSaved }) => {
  const [exams, setExams] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!book) return;
    setPicked((book.quizzes || []).map((q) => q.id));
    api.get("/exams").then((r) => {
      const own = new Set(r.data.map((e) => e.id));
      const extra = (book.quizzes || []).filter((q) => !own.has(q.id)).map((q) => ({ ...q, subject: "Linked by admin", questions: { length: q.question_count } }));
      setExams([...r.data, ...extra]);
    }).catch(() => {});
  }, [book]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/textbooks/${book.id}`, { quiz_exam_ids: picked });
      toast.success("Chapter quizzes updated");
      onSaved();
      onClose();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!book} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="quiz-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Chapter quizzes</DialogTitle>
          <DialogDescription>Every student who can read “{book?.title}” can take the quizzes you pick.</DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 -mx-1" data-testid="quiz-link-list">
          {exams.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">No exams yet. Create one first.</p>}
          {exams.map((e) => (
            <label key={e.id} className="flex items-center gap-3 px-1 py-2.5 cursor-pointer hover:bg-slate-50 rounded-lg">
              <Checkbox checked={picked.includes(e.id)} onCheckedChange={() => toggle(e.id)} data-testid={`quiz-link-check-${e.id}`} />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-slate-800 truncate">{e.title}</span>
                <span className="block text-xs text-slate-500">{e.subject} · {e.questions?.length || 0} questions</span>
              </span>
              {!e.is_published && <span className="text-[11px] font-medium text-amber-700 bg-amber-50 rounded-full px-2 py-0.5">Draft — hidden</span>}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="quiz-link-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="quiz-link-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
